import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import { CheckCircle, XCircle, Loader2, ArrowRight, Package } from 'lucide-react';
import { Button } from '../components/ui/button';
import { devLog, devError, devWarn } from '../utils/logger';

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [transaction, setTransaction] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId || !token) {
      setStatus('error');
      return;
    }

    const checkStatus = async () => {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        setTransaction(res.data);

        if (res.data.payment_status === 'paid') {
          setStatus('success');
        } else if (res.data.status === 'expired' || pollCount >= 10) {
          setStatus('error');
        } else {
          // Continue polling
          setPollCount(prev => prev + 1);
          setTimeout(checkStatus, 2000);
        }
      } catch (error) {
        devError('Error checking payment status:', error);
        if (pollCount >= 3) {
          setStatus('error');
        } else {
          setPollCount(prev => prev + 1);
          setTimeout(checkStatus, 2000);
        }
      }
    };

    checkStatus();
  }, [sessionId, token, pollCount]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center" data-testid="payment-loading">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-accent mx-auto mb-4" />
          <h2 className="font-playfair text-2xl font-bold text-foreground mb-2">
            Processing Payment...
          </h2>
          <p className="text-muted-foreground">Please wait while we confirm your payment.</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center" data-testid="payment-error">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="w-20 h-20 rounded-full bg-destructive/20 flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-10 h-10 text-destructive" />
          </div>
          <h2 className="font-playfair text-2xl font-bold text-foreground mb-2">
            Payment Issue
          </h2>
          <p className="text-muted-foreground mb-6">
            We couldn't confirm your payment. If you were charged, please contact support.
          </p>
          <div className="flex gap-4 justify-center">
            <Button variant="outline" onClick={() => navigate('/marketplace')}>
              Back to Marketplace
            </Button>
            <Button onClick={() => window.location.reload()} className="btn-gold">
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-12" data-testid="payment-success">
      <div className="max-w-2xl mx-auto px-6">
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6 animate-fadeIn">
            <CheckCircle className="w-10 h-10 text-emerald-500" />
          </div>
          <h1 className="font-playfair text-3xl md:text-4xl font-bold text-foreground mb-2 animate-slideUp">
            Payment Successful!
          </h1>
          <p className="text-muted-foreground animate-slideUp stagger-1">
            Thank you for your purchase on PatinaMart.
          </p>
        </div>

        {transaction && (
          <div className="bg-card/50 border border-border/20 rounded-lg p-6 mb-8 animate-slideUp stagger-2">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-lg bg-accent/20 flex items-center justify-center">
                <Package className="w-8 h-8 text-accent" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">{transaction.listing_title}</h3>
                <p className="text-sm text-muted-foreground">Order #{transaction.transaction_id}</p>
              </div>
            </div>

            <div className="border-t border-border/20 pt-4">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Total Paid</span>
                <span className="font-playfair text-2xl font-bold text-gold-gradient">
                  ${transaction.amount?.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="bg-card/50 border border-border/20 rounded-lg p-6 mb-8 animate-slideUp stagger-3">
          <h3 className="font-semibold text-foreground mb-4">What's Next?</h3>
          <ul className="space-y-3 text-sm text-muted-foreground">
            <li className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs text-accent font-bold">1</span>
              </div>
              <span>The seller has been notified of your purchase and will contact you shortly.</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs text-accent font-bold">2</span>
              </div>
              <span>You'll receive an email confirmation with the seller's contact details.</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs text-accent font-bold">3</span>
              </div>
              <span>Coordinate with the seller for pickup or delivery arrangements.</span>
            </li>
          </ul>
        </div>

        <div className="flex gap-4 justify-center animate-slideUp stagger-4">
          <Button variant="outline" onClick={() => navigate('/profile')} className="border-border/40">
            View My Orders
          </Button>
          <Button onClick={() => navigate('/marketplace')} className="btn-gold">
            Continue Shopping
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}
